import {useRef, useEffect, useState} from 'react';
import Slider from '@moredeal/react-slick';
import clsx from 'clsx';
import {MediaFile, getShopifyImageDimensions} from '@shopify/hydrogen/client';
import {Image} from '@shopify/hydrogen';
import type {MediaEdge} from '@shopify/hydrogen/storefront-api-types';
import {IconCarouselArrow} from '~/components';
import {ATTR_LOADING_EAGER} from '~/lib/const';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

/**
 * A client component that defines a media gallery for hosting images, 3D models, and videos of products
 */
export function ProductImageCarousel({
  media,
  className,
  inSale,
}: {
  media: MediaEdge['node'][];
  className?: string;
  inSale?: Boolean;
}) {
  if (!media.length) {
    return null;
  }

  const [processedMedia, setProcessedMedia] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [indexRange, setIndexRange] = useState([0, 3]);
  const [isMobile, setIsMobile] = useState(true);
  const [shownImages, setShownImages] = useState(4);
  const slider = useRef<any>(false);

  // Process media on media update.
  useEffect(() => {
    if (media.length) {
      setProcessedMedia(
        media.map((med) => {
          const data = {
            ...med,
            image: {
              // @ts-ignore
              ...med.image,
              altText: med.alt || 'Product image',
            },
          };

          return data;
        }),
      );

      setActiveIndex(0);
    }
  }, [media]);

  // On mount
  useEffect(() => {
    // Set slider ref
    slider.current = slider.current;

    // Handler to call on window resize
    function handleResize() {
      // Check if mobile
      if (window.innerWidth < 768) {
        setIsMobile(true);
        setShownImages(4);
      } else {
        setIsMobile(false);
        setShownImages(8);
      }
    }
    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (slider.current) {
      slider.current.slickGoTo(activeIndex);
    }

    if (activeIndex < indexRange[0]) {
      setIndexRange([indexRange[0] - 1, indexRange[1] - 1]);
    }

    if (activeIndex > indexRange[1]) {
      setIndexRange([indexRange[0] + 1, indexRange[1] + 1]);
    }
  }, [activeIndex]);

  // On window resize
  useEffect(() => {
    setIndexRange([indexRange[0], indexRange[0] + shownImages - 1]);
  }, [shownImages]);

  const renderBigMedia = (data) => {
    let mediaProps: Record<string, any> = {};

    switch (data.mediaContentType) {
      case 'IMAGE':
        mediaProps = {
          width: 800,
          widths: [400, 800, 1200, 1600, 2000, 2400],
        };
        break;
      case 'VIDEO':
        mediaProps = {
          width: '100%',
          autoPlay: true,
          controls: false,
          muted: true,
          loop: true,
          preload: 'auto',
        };
        break;
      case 'EXTERNAL_VIDEO':
        mediaProps = {width: '100%'};
        break;
      case 'MODEL_3D':
        mediaProps = {
          width: '100%',
          interactionPromptThreshold: '0',
          ar: true,
          loading: ATTR_LOADING_EAGER,
          disableZoom: true,
        };
        break;
    }

    if (data.mediaContentType === 'IMAGE') {
      mediaProps.loading = ATTR_LOADING_EAGER;
    }

    const {width: imgElementWidth, height: imgElementHeight} =
      getShopifyImageDimensions({
        data,
      });

    return (
      <a // Photoswipe lightbox open link
        href={data.image.url}
        data-pswp-width={data.image.width}
        data-pswp-height={data.image.height}
        key={'productGallery-' + data.id}
        target="_blank"
        rel="noreferrer"
        className="hover:cursor-zoom-in"
      >
        <MediaFile
          key={data.id}
          tabIndex="0"
          className={`w-full h-full fadeIn object-contain`}
          data={data}
          sizes={'(min-width: 64em) 60vw, (min-width: 48em) 50vw, 90vw'}
          // @ts-ignore
          options={{
            crop: 'center',
            scale: 2,
          }}
          {...mediaProps}
        />
      </a>
    );
  };

  const sliderSettings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    infinite: false,
    className: 'productGallerySlider',
    afterChange: (current: number) => {
      setActiveIndex(current);
    },
  };

  useEffect(() => {
    // Initialize Photoswipe lightbox
    if (processedMedia.length) {
      let lightbox = new PhotoSwipeLightbox({
        gallery: '.productGallerySlider',
        children: 'a',
        showHideAnimationType: 'fade',
        loop: false,
        pswpModule: () => import('photoswipe'),
      });
      lightbox.init();

      return () => {
        lightbox.destroy();
        lightbox = null;
      };
    }
  }, [processedMedia]);

  return (
    <div className={clsx('grid grid-cols-1 gap-4', className)}>
      {processedMedia.length > 0 && (
        <>
          <div className="-mx-3 md:mx-0 product-big-image bg-white">
            <Slider {...sliderSettings} ref={slider}>
              {processedMedia.map((media, index) => {
                return (
                  <div className="aspect-square" key={media.id}>
                    {renderBigMedia(media)}
                  </div>
                );
              })}
            </Slider>
          </div>
          {processedMedia.length > 1 && (
            <div
              className={clsx(
                'grid gap-2 w-full',
                isMobile ? 'grid-cols-4' : 'grid-cols-10',
              )}
            >
              {isMobile === false && (
                <div
                  className={`aspect-square h-15 w-15 flex justify-center items-center border col-start-1 row-start-1 ${
                    activeIndex > 0
                      ? 'cursor-pointer border-black bg-white'
                      : 'disabled'
                  }`}
                  onClick={() => {
                    if (activeIndex > 0) {
                      setActiveIndex(activeIndex - 1);
                    }
                  }}
                >
                  <IconCarouselArrow
                    customSize
                    direction="left"
                    className="relative inline-block h-5 w-5"
                  />
                </div>
              )}

              {processedMedia.map((med, index) => {
                const active = index === activeIndex;
                let scrollClass = '';
                const prefixer = isMobile ? 1 : 2;

                if (index >= indexRange[0] && index <= indexRange[1]) {
                  scrollClass = `col-start-${
                    prefixer + (index - indexRange[0])
                  } row-start-1`;
                } else {
                  scrollClass = 'hidden';
                }

                if (med.previewImage) {
                  return (
                    <div
                      key={med.id}
                      className={`aspect-square h-15 w-15 cursor-pointer relative snap-center ${scrollClass} ${
                        active ? 'shadow-inner' : ''
                      }`}
                      onClick={() => {
                        setActiveIndex(index);
                      }}
                    >
                      <Image
                        data={{
                          ...med.previewImage,
                          altText: med.alt || 'Product image',
                          width: '200px',
                          height: '200px',
                        }}
                        className="aspect-square object-contain bg-white"
                      />
                      {active && (
                        <div className="absolute w-full h-full bg-black bg-opacity-20 top-0 left-0"></div>
                      )}
                    </div>
                  );
                }
              })}
              {isMobile === false && (
                <div
                  className={`aspect-square h-15 w-15 col-start-10 row-start-1 flex justify-center items-center border border-black ${
                    activeIndex < processedMedia.length - 1
                      ? 'cursor-pointer border-black bg-white'
                      : 'disabled'
                  }`}
                  onClick={() => {
                    if (activeIndex < processedMedia.length - 1) {
                      setActiveIndex(activeIndex + 1);
                    }
                  }}
                >
                  {
                    <IconCarouselArrow
                      customSize
                      direction="right"
                      className="relative h-5 w-5 inline-block"
                    />
                  }
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
